/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import Slide from 'components/Slider/SingleSlide'
import Content from 'components/shared/Content'
import leftArrow from 'assets/icons/Arrow_left.svg'
import rightArrow from 'assets/icons/Arrow_right.svg'
import downArrow from 'assets/icons/Arrow_down.svg'
import Icon from 'components/shared/Icon'
import scrollToSection from 'utils/scrollToSection'
import Carousel from 'react-slick'
import useMedia from 'hooks/useMedia'

const Wrapper = styled.header`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  ${({ centered }) =>
    centered &&
    css`
      width: auto;
      height: auto;
    `}
`

const InnerContent = styled(Content)`
  max-width: 1500px;
`

const InnerWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`

const ControlBtn = styled.div`
  display: ${({ alwaysVisible }) => (alwaysVisible ? 'block' : 'none')};
  position: absolute;
  top: ${({ arrowsPosition }) => arrowsPosition};
  transform: ${({ arrowsPosition }) => `translateY(-${arrowsPosition})`};
  z-index: 8;
  background-color: rgba(0, 0, 0.4);
  width: 150px;
  height: 100px;
  border-radius: 50px;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.red};
  }
  ${({ theme }) => theme.mq.md} {
    display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
  }
`

const Prev = styled(ControlBtn)`
  left: -60px;
  background: url(${leftArrow}) no-repeat;
  background-position: 65% 50%;
  background-color: rgba(0, 0, 0, 0.4);
`

const Next = styled(ControlBtn)`
  right: -60px;
  background: url(${rightArrow}) no-repeat;
  background-position: 35% 50%;
  background-color: rgba(0, 0, 0, 0.4);
`

const IconWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
  width: 50px;
  height: 50px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`

const PromoWrapper = styled.div`
  display: none;
  ${({ theme }) => theme.mq.md} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
    text-align: right;
    padding: 20px 30px 20px 0;
  }
  ${({ theme }) => theme.mq.lg} {
    padding: 30px 30px 30px 0;
  }
`

const Info = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.bold};
  font-size: 1.3rem;
  text-transform: uppercase;
  text-align: left;
  line-height: 20px;
`

const Number = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.bold};
  font-size: 4.6rem;
  text-transform: uppercase;
  text-align: right;
  padding-right: 10px;
  line-height: 20px;
  ${({ theme }) => theme.mq.lg} {
    font-size: ${({ theme }) => theme.fontSize.xxl};
    padding-right: 20px;
  }
`

const defaultSettings = {
  infinite: true,
  draggable: false,
  slidesToShow: 1,
  speed: 900,
}

const Slider = ({
  arrowsPosition,
  carousel,
  settings,
  items,
  info,
  scrollTo,
}) => {
  const sliderRef = useRef(null)

  const slideToPrev = () => {
    sliderRef.current.slickPrev()
  }

  const slideToNext = () => {
    sliderRef.current.slickNext()
  }

  const slides = carousel
    ? items
    : items.map(({ src, alt, highlightedText, text }) => (
        <Slide
          key={text}
          highlightedText={highlightedText}
          image={src}
          imageAlt={alt}
          text={text}
        />
      ))

  const matchesLG = useMedia('(min-width: 1000px)')

  const sliderSettings = settings || defaultSettings

  return (
    <Wrapper centered={carousel}>
      {carousel !== 'centered' && carousel ? (
        <InnerContent>
          <Carousel ref={sliderRef} {...sliderSettings}>
            {slides}
          </Carousel>
        </InnerContent>
      ) : (
        <Carousel ref={sliderRef} {...sliderSettings}>
          {slides}
        </Carousel>
      )}
      <Prev
        arrowsPosition={arrowsPosition}
        isHidden={
          (slides.length <= 3 && carousel && matchesLG) ||
          (!carousel && slides.length < 2)
        }
        alwaysVisible={carousel === 'centered'}
        onClick={slideToPrev}
      />
      <Next
        arrowsPosition={arrowsPosition}
        isHidden={
          (slides.length <= 3 && carousel && matchesLG) ||
          (!carousel && slides.length < 2)
        }
        alwaysVisible={carousel === 'centered'}
        onClick={slideToNext}
      />
      {/* {info && (
        <InnerWrapper>
          <PromoWrapper>
            <Number>{info.number}</Number>
            <Info dangerouslySetInnerHTML={{ __html: info.text }} />
          </PromoWrapper>
        </InnerWrapper>
      )} */}
      {scrollTo && (
        <IconWrapper onClick={() => scrollToSection(scrollTo)}>
          <span>MORE</span>
          <Icon src={downArrow} alt="more" />
        </IconWrapper>
      )}
    </Wrapper>
  )
}

Slider.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  arrowsPosition: PropTypes.string,
  scrollTo: PropTypes.string,
  carousel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
}

Slider.defaultProps = {
  scrollTo: '',
  carousel: '',
  settings: null,
  arrowsPosition: '50%',
}

export default Slider
